<template>
  <div class="address-input">
    <multiselect
      ref="multiselect"
      label="name"
      group-values="data"
      group-label="type"
      :allowEmpty="false"
      :placeholder="computedPlaceholder"
      :internal-search="false"
      :track-by="trackBy"
      :value="computedValue"
      :options="computedOptions"
      :disabled="disabled"
      :class="{ ...classes, 'empty-search': search === '' }"
      @input="handleInput"
      @search-change="handleSearchChange"
      @open="handleOpen"
      @close="isOpen = false"
    >
      <!-- Hide the default caret -->
      <template #caret>
        <div />
      </template>

      <template #placeholder>
        <div class="d-flex gap-2 align-items-center">
          <i class="icon-outline_search" />
          <span class="__placeholder">
            {{ computedPlaceholder }}
          </span>
        </div>
      </template>

      <template #selection="{ isOpen }">
        <template v-if="isOpen">
          <i class="icon-outline_search" />
        </template>
      </template>

      <!-- If there is no option -->
      <template #noResult>
        <template v-if="isLoading || search === ''">
          <div />
        </template>
        <template v-else>
          {{ $t('fleetTaskInstant.text.noData') }}
        </template>
      </template>

      <!-- Observer to get next page data -->
      <template #afterList>
        <div v-if="isLoading || search === ''" class="px-2 py-2 d-flex gap-3 flex-column">
          <b-skeleton v-for="(i, idx) in ['100', '100']" :key="idx" :width="i" />
        </div>
        <div v-observe-visibility="handleElementObserved" style="height: 10px" />
      </template>

      <!-- Custom style of options -->
      <template #option="{ option }">
        <template v-if="option.$isLabel">
          <div class="__option-group">
            <div>
              {{ $t(option.$groupLabel) }}
            </div>
            <div>
              {{ option.$groupLabel === 'fleetTaskInstant.category.masterAddress' ? ` (${totalCount})` : '' }}
            </div>
          </div>
        </template>
        <template v-else>
          <div class="__option-list">
            <div class="__option-icon">
              <i :class="iconMappings[option.type]" />
            </div>
            <div class="__option-content">
              <div class="__address-name">
                {{ option.name }}
              </div>
              <div class="__address-full-name">
                {{ option.fullName }}
              </div>
            </div>
          </div>
        </template>
      </template>
    </multiselect>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { gmapApi } from 'vue2-google-maps'

import { GET_ADDRESSES } from '@/services/api/fleetTaskInstant'
import { debounce } from '@/helpers/debounce'
import { GOOGLE_MAP_ADDRESS_TYPE, MASTER_DATA_ADDRESS_TYPE } from '../lib/constants/types'

import { getPlaceDetail } from '@/services/api/google/placeDetail'
import { getPlacesAutocomplete } from '@/services/api/google/autocomplete'

export default {
  components: {
    Multiselect,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Object,
      default: () => {
        return {
          id: undefined,
          name: '',
          fullName: '',
          type: '',
          lat: null,
          lng: null,
        }
      },
    },
    classes: {
      type: Object,
      default: () => ({}),
    },
    /**
     * To position the address result above other elements using a fixed
     * positioning, add the class 'fixed-input-wrapper' to the element you want the
     * address result to appear above.
     */
    fixed: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'fleetTaskInstant.placeholder.searchDestinationOrChooseMap',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isLoading: false,
      isOpen: false,

      search: '',
      trackBy: 'id',

      page: 1,
      totalCount: 0,

      googleMapAddresses: [],
      masterAddresses: [],
      value: {},

      iconMappings: {
        [GOOGLE_MAP_ADDRESS_TYPE]: 'icon-outline_pin',
        [MASTER_DATA_ADDRESS_TYPE]: 'icon-outline_bookmark',
      },

      inputPosition: null,
    }
  },
  computed: {
    google: gmapApi,
    computedValue() {
      // set value null if there is no selected
      // so the placeholder will be shown
      if (!this.modelValue?.id) {
        return null
      }

      const newValue = { ...this.modelValue }

      // since key name is optional
      // set name from fullName to showing it in placeholder
      if (newValue.name === '') {
        newValue.name = newValue.fullName
      }

      return newValue
    },
    computedOptions() {
      return [
        {
          type: 'fleetTaskInstant.category.googleRecommendation',
          data: this.googleMapAddresses,
        },
        {
          type: 'fleetTaskInstant.category.masterAddress',
          data: this.masterAddresses,
        },
      ]
    },
    computedPlaceholder() {
      const translation = this.$t(this.placeholder)

      if (translation) {
        return translation
      }

      return this.placeholder
    }
  },
  watch: {
    inputPosition: {
      handler() {
        if (this.fixed) {
          this.updatePopupStyle()
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.fixed) {
      this.startPositionCheck()
    }
  },
  methods: {
    // using PascalCase because it will be used by parent component
    HandleOpen() {
      let multiselect = this.$refs.multiselect
      multiselect.activate()
    },

    handleOpen() {
      this.isOpen = true

      // set input value with selected value
      let multiselect = this.$refs.multiselect
      multiselect.updateSearch(this.computedValue?.name || '')
    },

    async getData(type = 'initial') {
      this.isLoading = true

      // dont get data if there is no search query
      if (this.search === '') {
        this.isLoading = false
        return
      }

      if (type === 'initial') {
        const suggestions = await getPlacesAutocomplete(this.search)

        const googleMapAddresses = []

        if (suggestions) {
          suggestions.map((item) => {
            const newObj = {
              id: item.placePrediction.placeId,
              name: item.placePrediction.structuredFormat.mainText.text,
              fullName: item.placePrediction.structuredFormat.secondaryText.text,
              type: GOOGLE_MAP_ADDRESS_TYPE,
            }
            googleMapAddresses.push(newObj)
          })
        }

        this.googleMapAddresses = googleMapAddresses
      } else if (type === 'next_page') {
        // set next page
        this.page += 1
      }

      // search address from master data
      const filter = {
        ownership: ['Customer', 'Own'],
        pagination: {
          page: this.page,
          show: 5,
        },
        search: this.search,
      }

      const addresses = await this.$apollo.query({
        query: GET_ADDRESSES,
        fetchPolicy: 'no-cache',
        variables: { filter },
      })

      if (!addresses.data?.geofences?.geofences.data) return

      // set master data results to state
      addresses.data.geofences.geofences.data.forEach((address) => {
        let lat, lng

        if (address.type === 'C') {
          [lat, lng] = this.convertCircDataToLatLng(address.circData)
        } else if (address.type === 'P') {
          [lat, lng] = this.convertPolyDataToLatLng(address.polyData)
        }

        const newObj = {
          id: address.geofenceId.toString(),
          name: address.fenceName,
          fullName: address.address,
          type: MASTER_DATA_ADDRESS_TYPE,
          lat,
          lng,
        }

        this.masterAddresses.push(newObj)
      })

      this.totalCount = addresses.data.geofences.geofences.pagination.total
      this.isLoading = false
    },

    handleSearchChange: debounce(async function (search) {
      this.search = search

      // reset options if search is changing
      this.googleMapAddresses = []
      this.masterAddresses = []
      this.page = 1
      this.totalCount = 0

      await this.$nextTick()

      let type = 'initial'
      if (this.page > 1) {
        type = 'next_page'
      }

      await this.getData(type)
    }, 500),

    handleElementObserved(isVisible) {
      if (!isVisible) return

      // if there is any search query, dont get any data
      if (this.totalCount === this.masterAddresses.length) return
      this.getData('next_page')
    },

    async handleInput(selected) {
      if (selected.type === GOOGLE_MAP_ADDRESS_TYPE) {
        const res = await getPlaceDetail(selected.id)
        selected = {
          ...selected,
          lat: res.latitude,
          lng: res.longitude,
        }
      }

      this.$emit('update:modelValue', selected)
    },

    convertCircDataToLatLng(circData) {
      const regex = /<\((-?\d+\.\d+),(-?\d+\.\d+)\),[-+]?[0-9]*\.?[0-9]+>/
      const match = circData.match(regex)

      return [parseFloat(match[1]), parseFloat(match[2])]
    },

    convertPolyDataToLatLng(polyData) {
      // Extract coordinates using regex pattern for the given format
      const coordinates = polyData.match(/-?\d+\.\d+/g)

      // Create bounds object
      const bounds = new this.google.maps.LatLngBounds()

      // Add each coordinate pair to bounds
      for (let i = 0; i < coordinates.length; i += 2) {
        const lat = parseFloat(coordinates[i])
        const lng = parseFloat(coordinates[i + 1])
        bounds.extend({ lat, lng })
      }

      // Return center point of the bounds
      return [bounds.getCenter().lat(), bounds.getCenter().lng()]
    },

    startPositionCheck() {
      const checkPosition = () => {
        if (this.$el) {
          const rect = this.$el.getBoundingClientRect()
          this.inputPosition = { top: rect.top, left: rect.left, bottom: rect.bottom }
        }
        requestAnimationFrame(checkPosition)
      }
      checkPosition()
    },

    updatePopupStyle() {
      if (!this.isOpen) return

      const detailSection = document.querySelector('.fixed-input-wrapper')
      if (!detailSection) return

      const isAbove = this.$refs.multiselect.$el.classList.contains('multiselect--above')
      const windowHeight = window.innerHeight
      const inputRect = this.$el.getBoundingClientRect()

      let multiselectContentWrapper = this.$refs.multiselect.$refs.list

      if (isAbove) {
        multiselectContentWrapper.style.bottom = `${windowHeight - this.inputPosition.top}px`
        multiselectContentWrapper.style.top = 'auto'
      } else {
        multiselectContentWrapper.style.top = `${inputRect.bottom}px`
        multiselectContentWrapper.style.bottom = 'auto'
      }

      multiselectContentWrapper.style.maxWidth = `${this.$refs.multiselect.$el.offsetWidth}px`
      multiselectContentWrapper.style.position = 'fixed'
      multiselectContentWrapper.style.left = `${this.inputPosition.left}px`
    },
  },
}
</script>

<style lang="scss" scoped>
.address-input {
  width: 100%;

  &__input-wrapper {
    display: flex;
  }

  &__clear-icon {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;

    position: absolute;
    right: 10px;
    top: 12px;
    font-size: 16px;
  }

  :deep(.multiselect) {
    .multiselect__tags {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-top: 10.5px !important;
      padding-right: 0 !important;
      padding-bottom: 10.5px !important;

      i {
        font-size: 16px;

        &:before {
          color: $mep-color-text-200;
        }
      }

      input {
        padding: 0px;
        padding-right: 40px;
        color: $mep-color-text-200;

        &::placeholder {
          color: $mep-color-text-200;
          font-size: 12px;
        }
      }

      .multiselect__placeholder {
        .__placeholder {
          color: $mep-color-text-200;
          font-size: 12px;
        }
      }
    }

    .multiselect__content {
      max-width: 300px;
    }

    .multiselect__element:not([role='option']) .multiselect__option--disabled {
      background-color: #fafcff !important;
      color: $mep-color-text-400 !important;
      border-top: 1px solid $mep-primary-100;
      border-bottom: 1px solid $mep-primary-100;
    }

    .multiselect__element {
      .__option-group {
        display: flex;
      }

      .__option-list {
        display: flex;
        overflow-wrap: anywhere;
        gap: 8px;

        .__option-icon {
          min-width: 24px;
          max-height: 24px;
          border-radius: 24px;
          background-color: $cyan-100;
          color: $mep-color-text-400;
          display: flex;
          align-items: center;
          justify-content: center;

          i {
            font-size: 16px;
          }
        }

        .__option-content {
          text-wrap: wrap;

          .__address-name {
            font-weight: 600;
            margin-bottom: 4px;
          }

          .__address-full-name {
            font-weight: 400;
          }
        }
      }
    }

    &.multiselect--active {
      .multiselect__tags {
        border-radius: 5px 5px 0 0;
        border: none;
        outline: 2px solid #d6e0ff;
      }
    }

    &.empty-search {
      &.multiselect--active {
        .multiselect__tags {
          border-radius: 5px;
        }
      }

      .multiselect__content-wrapper {
        display: none !important;
      }
    }
  }
}
</style>
